import { useEffect, useMemo, useRef } from "react";

import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary, ErrorFallback, usePusherChannel } from "@recruiter/shared";

import { basePusherConfig, sellerChannel } from "./config";
import { SellerSwitcher } from "./components/seller/seller-switcher";
import { ProjectDirectory } from "./components/seller/project-directory";
import { Inbox } from "./components/seller/inbox";
import { ThreadView } from "./components/seller/thread-view";
import { DebugNotePanel } from "./components/seller/debug-note-panel";
import { queryClient } from "./lib/query-client";
import { pathToRoute, routeForState, routeToPath } from "./lib/route-url";
import { applySellerEvent } from "./lib/seller-stream-dispatcher";
import { captureViewport } from "./lib/debug-screenshot";
import { postDebugNote } from "./lib/seller-client";
import { useSellerStore } from "./lib/store/seller-store";
import { SELLER_EVENT_NAMES, streamEventKey, type SellerLiveEvent } from "./lib/seller-stream";

// The talent test platform: a tester role-plays talents chatting live with the
// concierge (STERLING). One static bundle, its own origin, stub `x-seller-id`
// identity. Two panes: the inbox (left) and the open thread (right).
//
// Data layer (mirrors web): server state lives in the TanStack Query cache
// (lib/queries/seller.ts); UI/session state in the Zustand store
// (lib/store/seller-store.ts); live Pusher events fold into the cache through
// applySellerEvent — no pulse refetch.
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SellerWorkspace />
    </QueryClientProvider>
  );
}

function SellerWorkspace() {
  const activeSellerId = useSellerStore((s) => s.activeSellerId);
  const selectedThreadId = useSellerStore((s) => s.selectedThreadId);
  const setActiveSeller = useSellerStore((s) => s.setActiveSeller);
  const openSellerThread = useSellerStore((s) => s.openSellerThread);
  const clearActiveSeller = useSellerStore((s) => s.clearActiveSeller);
  const setSelectedThread = useSellerStore((s) => s.setSelectedThread);
  const debugMode = useSellerStore((s) => s.debugMode);
  const setDebugMode = useSellerStore((s) => s.setDebugMode);

  // Debug-mode capture: record the tester's note in the admin "Debug Notes" tab
  // (the talent-side twin of the buyer app's `submitDebugNote`). Reads identity
  // from the store (not reactive) and tags the note with which view it came from,
  // captures a best-effort screenshot, then posts — throwing on failure so the
  // panel surfaces it inline. Never intercepts the STERLING reply composer.
  const submitDebugNote = async (message: string): Promise<void> => {
    const state = useSellerStore.getState();
    const stage = !state.activeSellerId
      ? "talent:directory"
      : state.selectedThreadId
        ? "talent:thread"
        : "talent:inbox";
    // Best-effort + bounded: a failure/timeout yields null and the note still saves.
    const screenshot = await captureViewport();
    const res = await postDebugNote({
      note: message,
      sellerId: state.activeSellerId,
      stage,
      screenshot,
    });
    if (!res.ok) throw new Error(res.message);
  };

  const config = useMemo(() => basePusherConfig(activeSellerId), [activeSellerId]);
  const channel = activeSellerId ? sellerChannel(activeSellerId) : null;
  const events = usePusherChannel<SellerLiveEvent>(channel, SELLER_EVENT_NAMES, config);

  // De-dupe redelivery + StrictMode double-mounts by (turn_id, seq); fold each NEW
  // event into the Query cache via the dispatcher (which reads activeSellerId from
  // the store to key its writes). Replaces the old "bump a tick → re-GET" pulse.
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const ev of events) {
      const key = streamEventKey(ev);
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      applySellerEvent(ev);
    }
  }, [events]);

  // New channel on identity switch → forget the old channel's seen keys.
  useEffect(() => {
    seen.current = new Set();
  }, [activeSellerId]);

  // Keep the URL in sync with the store (deep links + back/forward, no router —
  // mirrors web's overlay sync in workspace-app.tsx). The pathname-equality
  // guard makes this a no-op when a popstate just drove the state change, so
  // push/pop never loop. The FIRST run replaces instead of pushing: boot
  // canonicalizes the URL ("/" with a stored identity → "/s/<id>", unknown path
  // → "/") without minting a history entry. `location.search` rides along so
  // `?debug=1` survives navigation.
  const bootSync = useRef(true);
  useEffect(() => {
    const desired = routeToPath(routeForState(activeSellerId, selectedThreadId));
    if (window.location.pathname !== desired) {
      const url = desired + window.location.search;
      if (bootSync.current) window.history.replaceState({}, "", url);
      else window.history.pushState({}, "", url);
    }
    bootSync.current = false;
  }, [activeSellerId, selectedThreadId]);

  // Back/Forward: parse the path and dispatch to the matching store action —
  // the three route kinds map 1:1 onto the three persisting actions, so
  // localStorage keeps mirroring the URL's identity. Unknown path → directory
  // (same fallback as boot). Mounted once for the SPA lifetime.
  useEffect(() => {
    const onPop = () => {
      const route = pathToRoute(window.location.pathname) ?? { kind: "directory" as const };
      const store = useSellerStore.getState();
      if (route.kind === "directory") store.clearActiveSeller();
      else if (route.kind === "inbox") store.setActiveSeller(route.sellerId);
      else store.openSellerThread(route.sellerId, route.threadId);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <div className={`sl-shell${debugMode ? " sl-shell--debug" : ""}`}>
      {debugMode ? (
        <div className="sl-debug-banner" role="status">
          <span className="sl-debug-banner__dot" aria-hidden="true" />
          <span className="sl-debug-banner__text">
            Debug mode is on. Use the note box to log what feels wrong on any page.
          </span>
          <button
            type="button"
            className="sl-debug-banner__off"
            onClick={() => setDebugMode(false)}
          >
            Turn off
          </button>
        </div>
      ) : null}
      <aside className="sl-sidebar">
        <div className="sl-brand">
          <div className="sl-brand__title">Talent workspace</div>
          <div className="sl-brand__sub">Test platform · chat with the concierge</div>
          <button
            type="button"
            className={`sl-debug-toggle${debugMode ? " sl-debug-toggle--on" : ""}`}
            onClick={() => setDebugMode(!debugMode)}
            aria-pressed={debugMode}
            title={
              debugMode
                ? "Debug mode ON. Log feedback notes for the team. Click to turn off."
                : "Turn on debug mode: log feedback notes for the team."
            }
          >
            <span className="sl-debug-toggle__dot" aria-hidden="true" />
            Debug
          </button>
        </div>
        <SellerSwitcher
          sellerId={activeSellerId}
          onSwitch={setActiveSeller}
          onBrowse={clearActiveSeller}
        />
        {activeSellerId ? (
          <ErrorBoundary
            resetKeys={[activeSellerId]}
            fallbackRender={({ resetErrorBoundary }) => (
              <ErrorFallback
                variant="inline"
                title="The inbox hit a snag"
                message="The open talent view is still available."
                primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
              />
            )}
          >
            <Inbox openThreadId={selectedThreadId} onOpen={setSelectedThread} />
          </ErrorBoundary>
        ) : null}
      </aside>
      <main className="sl-main">
        <ErrorBoundary
          resetKeys={[activeSellerId, selectedThreadId]}
          fallbackRender={({ resetErrorBoundary }) => (
            <ErrorFallback
              variant="section"
              title="This talent view hit a snag"
              message="The rest of the talent workspace is still available."
              primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
              secondaryAction={
                activeSellerId
                  ? { label: "Browse all projects", onClick: clearActiveSeller }
                  : undefined
              }
            />
          )}
        >
          {!activeSellerId ? (
            <ProjectDirectory onPickSeller={openSellerThread} />
          ) : selectedThreadId ? (
            <ThreadView threadId={selectedThreadId} />
          ) : (
            <div className="sl-center">
              <div className="sl-empty">
                Select a brief from the inbox to chat with the concierge.
              </div>
            </div>
          )}
        </ErrorBoundary>
      </main>
      {debugMode ? (
        <DebugNotePanel onSubmit={submitDebugNote} onTurnOff={() => setDebugMode(false)} />
      ) : null}
    </div>
  );
}
